(function () {
  'use strict';

  function initialize() {
    const root = document.getElementById('aoyama-guide');
    const configNode = document.getElementById('arvento-aoyama-config');
    if (!root || !configNode) return;
    let config;
    try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
    const { locale, ui, places, central } = config;
    const find = selector => root.querySelector(selector);
    const findAll = selector => Array.from(root.querySelectorAll(selector));
    const text = (key, values = {}) => ui[key].replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match);
    const make = (tag, content, className) => {
      const node = document.createElement(tag);
      if (content !== undefined) node.textContent = content;
      if (className) node.className = className;
      return node;
    };
    const button = (label, action) => {
      const node = make('button', text(label), 'atv-button atv-button-small');
      node.type = 'button';
      node.dataset.action = action;
      return node;
    };
    const link = (label, href) => {
      const node = make('a', text(label), 'atv-button atv-button-small');
      node.href = href;
      node.target = '_blank';
      node.rel = 'noopener noreferrer';
      return node;
    };
    const coords = point => `${point.lat.toFixed(6)}, ${point.lon.toFixed(6)}`;
    const mapUrl = point => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coords(point))}`;
    const walkUrl = point => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(coords(point))}&travelmode=walking`;
    const fold = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(locale).trim();
    const distance = (a, b) => {
      const radians = value => value * Math.PI / 180;
      const h = Math.sin(radians(b.lat - a.lat) / 2) ** 2 +
        Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) *
        Math.sin(radians(b.lon - a.lon) / 2) ** 2;
      return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
    };
    const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const metres = new Intl.NumberFormat(locale, { style: 'unit', unit: 'meter', unitDisplay: 'short', maximumFractionDigits: 0 });
    const kilometres = new Intl.NumberFormat(locale, { style: 'unit', unit: 'kilometer', unitDisplay: 'short', maximumFractionDigits: 1 });
    const formatDistance = value => value < 1000 ? metres.format(Math.round(value)) : kilometres.format(value / 1000);
    const source = point => point.kind === 'gps' ? text('gps_source', { m: number.format(point.accuracy) }) : text('manual_source');
    const manual = point => ({ ...point, kind: 'manual' });
    const cards = findAll('#place-results [data-place]');
    const entries = places.map((place, index) => ({ place, index, node: cards.find(card => card.dataset.place === place.id) }));
    const byId = new Map(places.map(place => [place.id, place]));
    const results = find('#place-results');
    const search = find('#place-search');
    const sort = find('#sort-places');
    const status = find('#location-status');
    const detail = find('#location-detail');
    const coordinateStatus = find('#coordinate-status');
    const selectedCoordinates = find('#selected-coordinates');
    const positionSource = find('#position-source');
    const shareStatus = find('#share-status');
    const returnPanel = find('#aoyama-return-reference');
    const mapPanel = make('div');
    mapPanel.id = 'aoyama-map-controls';
    find('#map-panel').append(mapPanel);
    const units = find('#distance-units');
    units.textContent = text('units');
    sort.setAttribute('aria-describedby', units.id);
    sort.replaceChildren();
    const editorialOption = make('option', text('editorial'));
    editorialOption.value = 'editorial';
    const distanceOption = make('option', text('closest'));
    distanceOption.value = 'distance';
    sort.append(editorialOption, distanceOption);
    find('#latitude').placeholder = text('latitude_hint');
    find('#longitude').placeholder = text('longitude_hint');
    let selected = null;
    let returnPoint = null;
    let mapPoint = null;
    let category = 'all';
    let generation = 0;
    let pending = false;

    function renderResults() {
      const query = fold(search.value);
      const ordered = entries.slice();
      if (selected && sort.value === 'distance') {
        ordered.sort((a, b) => distance(selected, a.place) - distance(selected, b.place) || a.index - b.index);
      }
      let count = 0;
      ordered.forEach(({ place, node }) => {
        const visible = (category === 'all' || place.categories.includes(category)) &&
          (!query || fold(`${place.name} ${place.search}`).includes(query));
        node.hidden = !visible;
        if (visible) count++;
        const distanceNode = node.querySelector('[data-distance]');
        distanceNode.textContent = selected ? text('distance', { distance: formatDistance(distance(selected, place)) }) : text('no_reference');
        distanceNode.setAttribute('aria-describedby', units.id);
        results.append(node);
      });
      find('#result-status').textContent = text('results', { n: number.format(count), order: text(sort.value === 'distance' ? 'closest' : 'editorial') });
      find('#empty-state').hidden = count !== 0;
      findAll('[data-action="filter"]').forEach(node => node.setAttribute('aria-pressed', String(node.dataset.filter === category)));
    }

    function renderSelection() {
      selectedCoordinates.value = selected ? coords(selected) : '';
      positionSource.textContent = selected ? source(selected) : text('cleared');
      distanceOption.disabled = !selected;
      sort.disabled = false;
      if (!selected) sort.value = 'editorial';
      ['copy', 'share', 'map-reference'].forEach(action => {
        find(`[data-action="${action}"]`).disabled = !selected;
      });
      find('[data-action="clear"]').disabled = !selected && !pending;
      find('#gps-button').setAttribute('aria-busy', String(pending));
      renderResults();
    }

    function select(point) {
      generation++;
      pending = false;
      selected = point;
      status.textContent = text('selected', { name: point.name });
      detail.textContent = source(point) + (distance(point, central) > 10000 ? ` ${text('outside')}` : '');
      coordinateStatus.textContent = '';
      shareStatus.textContent = '';
      renderSelection();
    }

    function renderReturn() {
      returnPanel.replaceChildren(make('p', returnPoint ? text('return_set', { name: returnPoint.name, coords: coords(returnPoint) }) : text('return_empty')));
      if (!returnPoint) return;
      const actions = make('div', undefined, 'atv-actions');
      actions.append(link('return_walk', walkUrl(returnPoint)), button('return_show', 'return-show'), button('return_clear', 'return-clear'));
      returnPanel.append(actions);
    }

    function renderMap() {
      mapPanel.replaceChildren();
      if (!mapPoint) {
        mapPanel.append(make('p', text('map_empty'), 'atv-privacy'));
        return;
      }
      mapPanel.append(make('h3', text('map_title', { name: mapPoint.name })), make('p', coords(mapPoint)),
        make('p', source(mapPoint), 'atv-privacy'), make('p', text('map_status'), 'atv-privacy'),
        make('p', text('map_disclosure'), 'atv-privacy'));
      const actions = make('div', undefined, 'atv-actions');
      actions.append(button('map_load', 'map-load'), link('map_open', mapUrl(mapPoint)));
      mapPanel.append(actions);
    }

    function showMap(point) {
      mapPoint = { ...point };
      renderMap();
      mapPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function clear() {
      generation++;
      pending = false;
      selected = null;
      mapPoint = null;
      status.textContent = text('cleared');
      detail.textContent = '';
      coordinateStatus.textContent = '';
      shareStatus.textContent = '';
      find('#latitude').value = '';
      find('#longitude').value = '';
      renderSelection();
      renderMap();
    }

    function requestGps() {
      const token = ++generation;
      pending = false;
      if (!window.isSecureContext) {
        status.textContent = text('gps_insecure');
        renderSelection();
        return;
      }
      if (!navigator.geolocation) {
        status.textContent = text('gps_unsupported');
        renderSelection();
        return;
      }
      pending = true;
      status.textContent = text('gps_pending');
      renderSelection();
      navigator.geolocation.getCurrentPosition(position => {
        if (generation !== token) return;
        select({ name: text('gps_name'), lat: position.coords.latitude, lon: position.coords.longitude,
          accuracy: position.coords.accuracy, kind: 'gps' });
      }, error => {
        if (generation !== token) return;
        pending = false;
        status.textContent = text(error.code === 1 ? 'gps_denied' : error.code === 3 ? 'gps_timeout' : 'gps_unavailable');
        renderSelection();
      }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 });
    }

    async function copy() {
      const token = generation;
      try {
        await navigator.clipboard.writeText(coords(selected));
        if (generation === token) shareStatus.textContent = text('copy_success');
      } catch (_) {
        if (generation === token) {
          shareStatus.textContent = text('copy_failure');
          selectedCoordinates.focus();
          selectedCoordinates.select();
        }
      }
    }

    async function share() {
      const token = generation;
      const point = { ...selected };
      try {
        if (navigator.share) {
          await navigator.share({ title: text('share_title'), text: text('share_text', {
            name: point.name, source: source(point), coords: coords(point)
          }), url: mapUrl(point) });
          if (generation === token) shareStatus.textContent = text('share_success');
        } else {
          await navigator.clipboard.writeText(mapUrl(point));
          if (generation === token) shareStatus.textContent = text('share_copied');
        }
      } catch (error) {
        if (generation === token) shareStatus.textContent = text(error.name === 'AbortError' ? 'share_cancel' : 'share_failure');
      }
    }

    find('#search-form').addEventListener('submit', event => { event.preventDefault(); renderResults(); });
    search.addEventListener('input', renderResults);
    sort.addEventListener('change', renderResults);
    find('#coordinate-form').addEventListener('submit', event => {
      event.preventDefault();
      const latitude = find('#latitude').value.trim();
      const longitude = find('#longitude').value.trim();
      const lat = Number(latitude);
      const lon = Number(longitude);
      if (!latitude || !longitude || !Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
        coordinateStatus.textContent = text('coordinate_error');
        return;
      }
      select(manual({ name: text('manual_name'), lat, lon }));
      coordinateStatus.textContent = text('coordinate_ready');
    });
    root.addEventListener('click', event => {
      const control = event.target.closest('[data-action]');
      if (!control || !root.contains(control) || control.disabled) return;
      const action = control.dataset.action;
      const place = byId.get(control.dataset.id);
      if (action === 'gps') requestGps();
      else if (action === 'central') select(manual({ ...central, name: text('central_name') }));
      else if (action === 'pick' && place) select(manual(place));
      else if (action === 'map-place' && place) showMap(manual(place));
      else if (action === 'set-return' && place) { returnPoint = manual(place); renderReturn(); }
      else if (action === 'filter') { category = control.dataset.filter; renderResults(); }
      else if (action === 'reset-filters') { category = 'all'; search.value = ''; sort.value = 'editorial'; renderResults(); }
      else if (action === 'map-reference' && selected) showMap(selected);
      else if (action === 'copy' && selected) copy();
      else if (action === 'share' && selected) share();
      else if (action === 'clear') clear();
      else if (action === 'return-show' && returnPoint) showMap(returnPoint);
      else if (action === 'return-clear') { returnPoint = null; renderReturn(); }
      else if (action === 'map-load' && mapPoint) {
        const frame = make('iframe');
        frame.title = text('map_iframe');
        frame.width = '100%';
        frame.height = '360';
        frame.referrerPolicy = 'no-referrer';
        frame.src = `https://www.google.com/maps?q=${encodeURIComponent(coords(mapPoint))}&z=15&output=embed&hl=${encodeURIComponent(locale)}`;
        control.disabled = true;
        mapPanel.append(frame);
      }
    });
    window.addEventListener('pagehide', () => {
      returnPoint = null;
      renderReturn();
      clear();
    });
    renderSelection();
    renderReturn();
    renderMap();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
